import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';

// Configure axios base URL for production
if (process.env.NODE_ENV === 'production') {
  axios.defaults.baseURL = '';
}

const SOURCE_FIELDS = [
  "product_id", "name", "manufacturers", "salt_composition",
  "medicine_type", "introduction", "benefits", "description",
  "how_to_use", "safety_advise", "if_miss", "packaging_detail",
  "mrp", "prescription_required", "fact_box", "primary_use",
  "storage", "common_side_effect", "alcohol_interaction",
  "pregnancy_interaction", "lactation_interaction", "driving_interaction",
  "kidney_interaction", "liver_interaction", "manufacturer_address",
  "q_a", "how_it_works", "reference", "image_url", "item_code",
  "dist_item_code", "package", "qty", "product_form", "gst_rate",
  "hsn_code", "product_category_name", "product_sub_category_name",
  "distributor", "distributor_mrp", "selling_price_incl_gst", "effective_customer_discount",
  "inventory_quantity", "deferred_allowed", "is_active", "info_tags",
  "rank", "brand", "tax_name", "tax_id"
];

const GENERIC_KEYWORDS = ['medicine'];
const FORM_TERMS = [
  'tablet', 'syrup', 'capsule', 'lotion', 'tube', 'bottle', 'gel',
  'syringe', 'nebulizer', 'pellets', 'strip', 'needle', 'soap',
  'ointment', 'cream', 'injection'
];

const createFunctionScoreClause = ({
  queryClause,
  weight,
  rankFactor,
  inventoryWeight,
  rankModifier = 'reciprocal',
  rankMissing = 100000
}) => {
  if (!queryClause) {
    return null;
  }

  return {
    function_score: {
      query: queryClause,
      functions: [
        ...(weight !== undefined ? [{ weight }] : []),
        ...(rankFactor !== undefined
          ? [{
              field_value_factor: {
                field: 'rank',
                factor: rankFactor,
                modifier: rankModifier,
                missing: rankMissing
              }
            }]
          : []),
        ...(inventoryWeight !== undefined
          ? [{
              filter: {
                range: {
                  inventory_quantity: { gt: 0 }
                }
              },
              weight: inventoryWeight
            }]
          : [])
      ],
      score_mode: 'sum',
      boost_mode: 'sum'
    }
  };
};

const buildCatalogueSearchQuery = (
  rawQuery,
  {
    page = 1,
    pageSize = 48,
    minScore = 0.3,
    category,
    distributor,
    priceMin,
    priceMax,
    strength = ''
  } = {}
) => {
  const query = (rawQuery || '').trim();
  const qLower = query.toLowerCase();

  const genericSet = new Set([...GENERIC_KEYWORDS, ...FORM_TERMS].map(t => t.toLowerCase()));
  const tokens = qLower.split(/[^a-z0-9%+.\-]+/).filter(Boolean);

  const kept = tokens.filter((token) => {
    const root = token.replace(/(es|s)$/i, '');
    return !(genericSet.has(token) || genericSet.has(root));
  });

  const cleanQuery = kept.join(' ').trim();
  const base = cleanQuery || qLower;

  const derivePhrase = (value) => {
    const parts = String(value || '')
      .split(/\s+/)
      .filter(word => word.length >= 2 && !/\d/.test(word));
    return parts.join(' ');
  };

  const phrase = derivePhrase(base);

  const baseWords = base.split(/\s+/).filter(Boolean);
  const brandNameCandidate = baseWords.length === 0
    ? base
    : baseWords[0].length > 2
      ? baseWords[0]
      : baseWords.length >= 2
        ? `${baseWords[0]} ${baseWords[1]}`
        : baseWords[0];
  const brandName = (brandNameCandidate || base).toLowerCase();

  const potencyMatches = (base.match(/\d+(?:\.\d+)?/g) || []);
  const potency = potencyMatches.join('*');

  const form = FORM_TERMS.find((term) => {
    const pattern = new RegExp(`\\b${term}\\b`, 'i');
    return pattern.test(query);
  }) || '';

  const shouldClauses = [];

  const addClause = (clause) => {
    if (!clause) return;
    const { function_score: fs } = clause;
    if (!fs || !fs.query) return;
    if (fs.query.bool && Array.isArray(fs.query.bool.should) && fs.query.bool.should.length === 0) return;
    shouldClauses.push(clause);
  };

  // Clause 1
  const clause1Should = [];
  if (phrase) {
    clause1Should.push({
      match: {
        name: {
          query: phrase,
          boost: 2
        }
      }
    });
  }
  if (brandName) {
    clause1Should.push({
      match: {
        name: {
          query: brandName,
          boost: 2
        }
      }
    });
  }
  if (clause1Should.length) {
    addClause(createFunctionScoreClause({
      queryClause: { bool: { should: clause1Should } },
      weight: 50,
      rankFactor: 0.0001,
      inventoryWeight: 2
    }));
  }

  // Clause 2
  if (phrase) {
    addClause(createFunctionScoreClause({
      queryClause: {
        match: {
          "c3.values": {
            query: phrase,
            boost: 5
          }
        }
      },
      weight: 100,
      rankFactor: 0.001,
      inventoryWeight: 2
    }));
  }

  // Clause 3
  if (query) {
    addClause(createFunctionScoreClause({
      queryClause: {
        match_phrase: {
          name: {
            query,
            boost: 3
          }
        }
      },
      weight: 50,
      rankFactor: 0.0001,
      inventoryWeight: 2
    }));
  }

  // Clause 4
  if (brandName) {
    addClause(createFunctionScoreClause({
      queryClause: {
        match: {
          brand: {
            query: brandName,
            boost: 10
          }
        }
      },
      weight: 500,
      rankFactor: 0.001,
      inventoryWeight: 1.2
    }));
  }

  // Clause 5
  const clause5Should = [];
  if (query) {
    clause5Should.push({
      match_phrase: {
        salt_composition: {
          query,
          boost: 5
        }
      }
    });
  }
  if (brandName) {
    clause5Should.push({
      match_phrase: {
        salt_composition: {
          query: brandName,
          boost: 10
        }
      }
    });
  }
  if (clause5Should.length) {
    addClause(createFunctionScoreClause({
      queryClause: {
        bool: {
          should: clause5Should
        }
      },
      weight: 100,
      rankFactor: 0.001,
      inventoryWeight: 1.2
    }));
  }

  // Clause 6
  if (query) {
    addClause(createFunctionScoreClause({
      queryClause: {
        match_phrase_prefix: {
          "c3.values": {
            query,
            boost: 3
          }
        }
      },
      weight: 25,
      rankFactor: 0.001,
      inventoryWeight: 1.2
    }));
  }

  // Clause 7
  if (form) {
    addClause(createFunctionScoreClause({
      queryClause: {
        match_phrase: {
          product_form: {
            query: form,
            boost: 100
          }
        }
      },
      weight: 1000,
      rankFactor: 1,
      inventoryWeight: 2
    }));
  }

  // Clause 8
  if (query) {
    addClause(createFunctionScoreClause({
      queryClause: {
        match_phrase_prefix: {
          name: {
            query,
            boost: 2.5
          }
        }
      },
      weight: 20,
      rankFactor: 0.1,
      inventoryWeight: 1.5
    }));
  }

  // Clause 9
  if (query) {
    addClause(createFunctionScoreClause({
      queryClause: {
        prefix: {
          "name.keyword": {
            value: query,
            boost: 2
          }
        }
      },
      weight: 15,
      rankFactor: 0.1,
      inventoryWeight: 1.5
    }));
  }

  // Clause 10
  if (query) {
    const clause10Should = [];

    clause10Should.push({
      match: {
        name: {
          query,
          boost: 4,
          fuzziness: 'AUTO'
        }
      }
    });

    if (potency) {
      clause10Should.push({
        wildcard: {
          name: {
            value: `*${potency}*`,
            boost: 3
          }
        }
      });
    }

    clause10Should.push({
      wildcard: {
        salt_composition: {
          value: `*${query}*`,
          boost: 1
        }
      }
    });

    clause10Should.push({
      match: {
        "name.ngram": {
          query,
          boost: 2,
          minimum_should_match: '75%'
        }
      }
    });

    clause10Should.push({
      match: {
        "name.edge_ngram": {
          query,
          boost: 2,
          minimum_should_match: '75%'
        }
      }
    });

    clause10Should.push({
      match: {
        salt_composition: {
          query,
          boost: 1.5,
          fuzziness: 'AUTO'
        }
      }
    });

    if (brandName) {
      clause10Should.push({
        match: {
          brand: {
            query: brandName,
            boost: 4.5,
            fuzziness: 'AUTO'
          }
        }
      });
    }

    if (phrase) {
      clause10Should.push({
        match_phrase: {
          name: {
            query: phrase,
            boost: 2.5
          }
        }
      });
    }

    clause10Should.push({
      match: {
        "c3.values": {
          query,
          boost: 1.25,
          fuzziness: 'AUTO'
        }
      }
    });

    addClause(createFunctionScoreClause({
      queryClause: {
        bool: {
          should: clause10Should
        }
      },
      weight: 7,
      rankFactor: 0.1,
      inventoryWeight: 1.2
    }));
  }

  // Clause 11
  if (query) {
    const clause11Should = [
      {
        match: {
          name: {
            query,
            boost: 4,
            fuzziness: 'AUTO'
          }
        }
      },
      {
        wildcard: {
          name: {
            value: `*${query}*`,
            boost: 1
          }
        }
      },
      {
        match: {
          "name.ngram": {
            query,
            boost: 2,
            minimum_should_match: '50%'
          }
        }
      },
      {
        match: {
          "name.edge_ngram": {
            query,
            boost: 2,
            minimum_should_match: '75%'
          }
        }
      },
      {
        match: {
          salt_composition: {
            query,
            boost: 2,
            fuzziness: 'AUTO'
          }
        }
      }
    ];

    if (phrase) {
      clause11Should.push({
        match_phrase: {
          name: {
            query: phrase,
            boost: 2.5
          }
        }
      });
    }

    addClause(createFunctionScoreClause({
      queryClause: {
        bool: {
          should: clause11Should
        }
      },
      weight: 5,
      rankFactor: 0.1,
      inventoryWeight: 1.2
    }));
  }

  const filterConditions = [
    { term: { is_active: true } }
  ];

  if (category) {
    filterConditions.push({ term: { "category.keyword": category } });
  }

  if (distributor) {
    filterConditions.push({ term: { "distributor.keyword": distributor } });
  }

  if (priceMin !== undefined && priceMin !== null) {
    filterConditions.push({ range: { plazza_selling_price_incl_gst: { gte: priceMin } } });
  }

  if (priceMax !== undefined && priceMax !== null) {
    filterConditions.push({ range: { plazza_selling_price_incl_gst: { lte: priceMax } } });
  }

  const boolQuery = {
    should: shouldClauses,
    minimum_should_match: 1
  };

  if (filterConditions.length) {
    boolQuery.filter = filterConditions;
  }

  const fromValue = Math.max(page - 1, 0) * pageSize;

  const esQuery = {
    query: {
      bool: boolQuery
    },
    min_score: minScore,
    from: fromValue,
    size: pageSize,
    track_scores: true,
    _source: SOURCE_FIELDS
  };

  return esQuery;
};

function CatalogueSearch({ authData }) {
  const PAGE_SIZE = 48;
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [distributorDetails, setDistributorDetails] = useState(null);
  const [loadingDistributor, setLoadingDistributor] = useState(false);
  const [inventoryDetails, setInventoryDetails] = useState(null);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const latestQueryRef = useRef('');
  const latestRequestIdRef = useRef(null);

  const loadCataloguePage = useCallback(async (queryText, pageToLoad = 1, append = false) => {
    const trimmedQuery = (queryText || '').trim();
    if (!trimmedQuery) {
      if (!append) {
        setProducts([]);
        setLoading(false);
        setHasMore(false);
        setPage(1);
      }
      return;
    }

    const requestId = Symbol('catalogueRequest');
    if (!append) {
      latestRequestIdRef.current = requestId;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const esQuery = buildCatalogueSearchQuery(trimmedQuery, {
        page: pageToLoad,
        pageSize: PAGE_SIZE
      });

      const response = await axios.post(`/api/catalogue/search`, esQuery, {
        headers: {
          phone: authData.phone,
          otp: authData.otp
        }
      });

      const hits = response.data.hits?.hits || [];
      const productData = hits.map(hit => hit._source);
      const totalHits = response.data.hits?.total?.value || 0;

      const isLatestQuery = trimmedQuery === latestQueryRef.current;
      const isLatestRequest = append || latestRequestIdRef.current === requestId;

      if (isLatestQuery && isLatestRequest) {
        setProducts(prevProducts => {
          if (!append) {
            return productData;
          }

          const existingIds = new Set(prevProducts.map(item => item.product_id));
          const newItems = productData.filter(item => !existingIds.has(item.product_id));
          return [...prevProducts, ...newItems];
        });

        const newHasMore = pageToLoad * PAGE_SIZE < totalHits;
        setHasMore(newHasMore);
        setPage(pageToLoad);

        console.log('Catalogue - Total hits:', totalHits);
        console.log('Catalogue - Hits returned:', hits.length);
        console.log('Catalogue - Products set:', productData.length);
      }
    } catch (err) {
      console.error('Catalogue search error:', err);
      if (!append) {
        setError('Failed to search catalogue. Please try again later.');
        setProducts([]);
        setHasMore(false);
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else if (latestRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [authData.phone, authData.otp, PAGE_SIZE]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmedQuery = searchQuery.trim();
      if (!trimmedQuery) {
        setProducts([]);
        setHasMore(false);
        setPage(1);
        setLoading(false);
        setLoadingMore(false);
        setError(null);
        latestQueryRef.current = '';
        return;
      }

      setPage(1);
      setHasMore(false);
      latestQueryRef.current = trimmedQuery;
      loadCataloguePage(trimmedQuery, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, loadCataloguePage]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        !loading &&
        !loadingMore &&
        hasMore
      ) {
        loadCataloguePage(latestQueryRef.current, page + 1, true);
      }
    }, {
      rootMargin: '200px'
    });

    const sentinel = sentinelRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, loadCataloguePage, page]);

  const fetchDistributorDetails = async (itemCode) => {
    if (!itemCode) {
      setDistributorDetails(null);
      return;
    }

    setLoadingDistributor(true);
    try {
      const response = await axios.get(`/api/distributor/${encodeURIComponent(itemCode)}`, {
        headers: {
          phone: authData.phone,
          otp: authData.otp
        }
      });
      setDistributorDetails(response.data.data);
    } catch (error) {
      console.error('Error fetching distributor details:', error);
      setDistributorDetails(null);
    } finally {
      setLoadingDistributor(false);
    }
  };

  const fetchInventoryDetails = async (productId) => {
    if (!productId) {
      setInventoryDetails(null);
      return;
    }

    setLoadingInventory(true);
    try {
      const response = await axios.get(`/api/inventory/${encodeURIComponent(productId)}`, {
        headers: {
          phone: authData.phone,
          otp: authData.otp
        }
      });
      setInventoryDetails(response.data.data);
    } catch (error) {
      console.error('Error fetching inventory details:', error);
      setInventoryDetails(null);
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    // Fetch distributor details if item_code is available
    const itemCode = product.item_code || product.dist_item_code;
    fetchDistributorDetails(itemCode);
    // Fetch inventory details using product_id
    fetchInventoryDetails(product.product_id);
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setDistributorDetails(null);
    setInventoryDetails(null);
  };

  return (
    <div className="container">
      <div className="search-container">
        <h1>Catalogue Search</h1>
        <p className="search-subtitle">Search products in the catalogue index</p>
        <input
          type="text"
          className="search-input"
          placeholder="Search catalogue products by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {loading && <div className="loading">Searching catalogue...</div>}
      
      {error && <div className="error">{error}</div>}

      {!loading && !error && products.length > 0 && (
        <>
          <div className="products-grid">
            {products.map((product) => (
              <ProductCard
                key={product.product_id}
                product={product}
                onClick={() => handleProductClick(product)}
              />
            ))}
          </div>
          <div ref={sentinelRef} className="infinite-scroll-sentinel" />
          {loadingMore && (
            <div className="loading">Loading more catalogue products...</div>
          )}
        </>
      )}

      {!loading && !error && searchQuery && products.length === 0 && (
        <div className="loading">No products found in catalogue</div>
      )}

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          distributorDetails={distributorDetails}
          loadingDistributor={loadingDistributor}
          inventoryDetails={inventoryDetails}
          loadingInventory={loadingInventory}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

export default CatalogueSearch; 